import { AxiosRequestConfig, CancelToken } from "axios";
import api from "src/spintr/SpintrApi";

const listsBaseUrl = "/api/v1/QualityHubTaskLists";
const groupsBaseUrl = "/api/v1/QualityHubTaskListGroups";
const tasksBaseUrl = "/api/v1/QualityHubTaskListItems";
const statusBaseUrl = "/api/v1/QualityHubTaskListItemStatuses";
const resourceTagsBaseUrl = "/api/v1/ResourceTags";
const operationalTargetsBaseUrl = "/api/v1/OperationalTargets";

/**
 * The input model expected by the API for creating or editing a task list.
 */
export type TaskListModel
    = Omit<Spintr.TaskList, "id" | "createdOn" | "editedOn">
    & { };

/**
 * The input model expected by the API for creating or editing a task group.
 */
export type TaskGroupModel = Omit<Spintr.TaskListGroup,
    "id" | "tasks"
>;

/**
 * The input model expected by the API when creating or editing a task.
 */
export type TaskModel
    = Omit<Spintr.TaskListTask, "id" | "tags" | "createdOn" | "assignments">
    & {
        /**
         * The IDs of the tags associated with the task.
         */
        tagIds: number[];

        /**
         * The IDs of the assignees for the task.
         */
        assigneeIds?: number[];
    };

export type TaskActionModel
    = Omit<Spintr.TaskListTask, "id" | "tags" | "listId" | "createdOn" | "assignments">
    & {
        files?: File[];

        /**
         * The IDs of the assignees for the task.
         */
        assigneeIds?: number[];
    };

export type TaskStatusModel = Omit<Spintr.TaskListItemStatus,
    "id" | "reportedOn" | "reportedBy" | "uberId"
> & {
    files?: File[];

    pinCode?: string;

    reporterId?: number;

    artifactValue?: string | undefined;

    /**
     * Local-only identifier used to correlate optimistic signature queue state.
     * Not serialized to the API.
     */
    signatureQueueItemId?: string;
};

export type QualityHubPinHash = {
    pinCodeHash: string;
};

export type OperationalTargetModel = Omit<Spintr.OperationalTarget, "id" | "uberId"> & {
    nodeId: number;
};

export type OperationalTargetResponse = Omit<Spintr.OperationalTarget, "startDate" | "endDate"> & {
    startDate: string;

    endDate?: string | null | undefined;
}

/**
 * The input model expected by the API when creating or editing a resource tag.
 */
export type ResourceTagModel = Omit<Spintr.ResourceTag, "id">;

function fixStatusDate(status: Spintr.TaskListItemStatus): Spintr.TaskListItemStatus {
    return {
        ...status,
        reportedOn: new Date(status.reportedOn),
    };
}

export function fixTaskListItemDates(taskListItem: Spintr.TaskListTask): Spintr.TaskListTask {
    return {
        ...taskListItem,
        createdOn: new Date(taskListItem.createdOn),
        editedOn: taskListItem.editedOn ? new Date(taskListItem.editedOn) : undefined,
        dueByDate: taskListItem.dueByDate ? new Date(taskListItem.dueByDate) : undefined,
    };
}

function fixTaskListDates(taskList: Spintr.TaskList): Spintr.TaskList {
    return {
        ...taskList,
        createdOn:  new Date(taskList.createdOn),
        startDate:  new Date(taskList.startDate),
        recurrenceReferenceDate: taskList.recurrenceReferenceDate
            ? new Date(taskList.recurrenceReferenceDate)
            : new Date(taskList.startDate),
        endDate:    taskList.endDate ? new Date(taskList.endDate) : undefined,
        statuses:   taskList.statuses?.map(fixStatusDate),
        tasks:      taskList.tasks?.map(fixTaskListItemDates),
    };
}

export async function queryTaskListsAsync(
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<Spintr.TaskList>> {
    const response = await api.get<Spintr.QueryEnvelope<Spintr.TaskList>>(
        listsBaseUrl, {
        cancelToken,
    });

    return {
        ...response.data,
        items: response.data.items
            .map(fixTaskListDates)
            .filter((list) => list.listType !== 3 && list.listTarget !== 1),
    };
}

export async function findTaskListAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    const response = await api.get<Spintr.TaskList>(
        `${listsBaseUrl}/${id}`, {
        cancelToken,
    });

    const taskList = response.data;

    return fixTaskListDates(taskList);
}

/**
 * Resolves the action list that a follow-up action created from the given
 * source list would land in. Eagerly creates the action list if one does
 * not yet exist for the resolved owner.
 */
export async function findActionListForSourceAsync(
    sourceTaskListId: number,
    nodeId?: number,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    const response = await api.get<Spintr.TaskList>(
        `${listsBaseUrl}/${sourceTaskListId}/action-list`, {
        cancelToken,
        params: nodeId !== undefined ? { nodeId } : undefined,
    });

    return fixTaskListDates(response.data);
}

export async function createTaskListAsync(
    model: TaskListModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    const response = await api.post<Spintr.TaskList>(listsBaseUrl, model, {
        cancelToken,
    });

    const taskList = response.data;

    return fixTaskListDates(taskList);
}

export async function updateTaskListAsync(
    id: number,
    model: TaskListModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    const response = await api.put<Spintr.TaskList>(
        `${listsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    const taskList = response.data;

    return fixTaskListDates(taskList);
}

/**
 * The parameters for querying the user's task lists.
 */
export type QueryMyTaskListsParams = {
    /**
     * The ID of the Quality Hub node to load task lists for.
     */
    nodeId?: number | undefined;
    
    /**
     * The date to filter task lists as of.
     */
    asOf?: Date | undefined;
};

/**
 * Queries the API for the user's task lists.
 *
 * @param {QueryMyTaskListsParams} [params={}] - The query parameters.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskList[]>} - A promise that resolves to the task lists.
 */
export async function queryMyTaskListsAsync(
    params: QueryMyTaskListsParams = {},
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList[]> {
    const response = await api.get<Spintr.QueryEnvelope<Spintr.TaskList>>(
        `${listsBaseUrl}/my`,
        {
            cancelToken,
            params,
        });

    return response.data.items.map(fixTaskListDates);
}

/**
 * Creates a task list item asynchronously.
 *
 * @param {TaskModel} model - The task model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 */
export async function createTaskListItemAsync(
    model: TaskModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListTask> {
    const formData = modelToFormData(model);
    
    const response = await api.post<Spintr.TaskListTask>(
        tasksBaseUrl,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return fixTaskListItemDates(response.data);
}

function writeFormDataPair(key: string, value: unknown, fd: FormData): void {
    if (value === null || value === undefined) {
        return;
    }

    if (value instanceof File) {
        fd.append(key, value, value.name);
        return;
    }

    if (value instanceof Date) {
        fd.append(key, value.toISOString());
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((v) => writeFormDataPair(key, v, fd));
        return;
    }

    return fd.append(key, value.toString());
}

function modelToFormData<TModel extends { [s: string]: unknown; }>(model: TModel): FormData {
    const formData = new FormData();

    Object
        .entries(model)
        .forEach(([key, value]) => writeFormDataPair(key, value, formData));

    return formData;
}

/**
 * Updates a task list item asynchronously.
 *
 * @param {number} id - The ID of the task list item to be updated.
 * @param {TaskModel} model - The task model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListTask>} - The updated task list item.
 */
export async function updateTaskListItemAsync(
    id: number,
    model: TaskModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListTask> {
    const formData = modelToFormData(model);

    const response = await api.put<Spintr.TaskListTask>(
        `${tasksBaseUrl}/${id}`,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return fixTaskListItemDates(response.data);
}

export async function setTaskListGroupOverridesAsync(
    listId: number,
    groupOverrides: Record<string, Partial<Spintr.TaskListGroup>>,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.put<void>(
        `${listsBaseUrl}/${listId}/overrides/groups`, groupOverrides, {
        cancelToken,
    });
}

/**
 * Updates multiple task list item overrides asynchronously.
 *
 * @param {number} listId - The ID of the task list.
 * @param {Record<string, Partial<Spintr.TaskListTask>>} overrides - The task overrides to apply.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<void>} - A promise that resolves when the overrides are updated.
 */
export async function updateTaskListItemOverridesAsync(
    listId: number,
    overrides: Record<string, Partial<Spintr.TaskListTask>>,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.put<void>(
        `${listsBaseUrl}/${listId}/overrides/items`, overrides, {
        cancelToken,
    });
}

/**
 * Creates a task list action asynchronously.
 *
 * @param {number} parentTask - The ID of the parent task for the action.
 * @param {TaskActionModel} model - The task action model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListTask>} - The created task list action.
 */
export async function createTaskListActionAsync(
    parentTask: number,
    model: TaskActionModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListTask> {
    const formData = modelToFormData(model);

    const response = await api.post<Spintr.TaskListTask>(
        `${tasksBaseUrl}/${parentTask}/actions`,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return fixTaskListItemDates(response.data);
}

/**
 * Create a task list group asynchronously.
 *
 * @param {TaskGroupModel} model - The task group model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListGroup>} - The created task list group.
 */
export async function createTaskListGroupAsync(
    model: TaskGroupModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListGroup> {
    const response = await api.post<Spintr.TaskListGroup>(
        groupsBaseUrl, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Updates a task list group asynchronously.
 *
 * @param {number} id - The ID of the task list group to be updated.
 * @param {TaskGroupModel} model - The task group model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListGroup>} - The updated task list group.
 */
export async function updateTaskListGroupAsync(
    id: number,
    model: TaskGroupModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListGroup> {
    const response = await api.put<Spintr.TaskListGroup>(
        `${groupsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * A verification file (image or video) attached to a task list item status.
 */
export type TaskListStatusVerification = {
    /**
     * The unique identifier of the verification.
     */
    id: string;

    /**
     * The ID of the parent task list item status.
     */
    statusId: number;

    /**
     * The ID of the instance the verification belongs to.
     */
    instanceId: number;

    /**
     * The kind of verification — one of {@link SpintrTypes.QualityHubVerificationType}.
     */
    verificationType: number;

    /**
     * The stored filename of the verification blob.
     */
    filename: string;

    /**
     * The original display name of the verification file.
     */
    displayName: string;

    /**
     * The size of the verification blob in bytes.
     */
    sizeInBytes: number;
};

/**
 * A {@link Spintr.TaskListItemStatus} enriched with the optional fields the
 * verification archive endpoints return.
 */
export type TaskListItemStatusWithDetails = Spintr.TaskListItemStatus & {
    /**
     * The artifact value reported for the status, if any.
     */
    artifactValue?: string | undefined;

    /**
     * The verification files attached to the status, if any.
     */
    verifications?: TaskListStatusVerification[] | undefined;
};

/**
 * Per-station aggregation of submitted verification files within a window.
 */
export type StationVerificationCount = {
    uberId: number;
    verificationCount: number;
};

/**
 * Per-task aggregation of submitted verification files within a window.
 */
export type TaskVerificationCount = {
    itemId: number;
    verificationCount: number;
};

/**
 * The query parameters accepted by {@link queryTaskListItemStatusesAsync}.
 */
export type QueryTaskListItemStatusesParams = {
    /**
     * Optionally restrict the query to a single task list. When omitted,
     * statuses across every list the caller can see are returned.
     */
    listId?: number | undefined;

    /**
     * Inclusive lower bound on `reportedOn`.
     */
    dateFrom?: Date | undefined;

    /**
     * Exclusive upper bound on `reportedOn`.
     */
    dateTo?: Date | undefined;

    /**
     * Scope the query to statuses reported against the given node — both the
     * node's own Uber and any legacy referenced Uber (department/office) are
     * matched.
     */
    nodeId?: number | undefined;

    /**
     * Scope the query to a single Uber id (e.g. a station the user clicked
     * in the context list). Takes precedence over `nodeId`.
     */
    uberId?: number | undefined;

    /**
     * Restrict the query to a single task list item.
     */
    itemId?: number | undefined;

    /**
     * When true, only statuses with at least one verification file are returned.
     */
    withVerifications?: boolean | undefined;

    /**
     * The number of statuses to skip.
     */
    skip?: number | undefined;

    /**
     * The maximum number of statuses to take.
     */
    take?: number | undefined;
};

/**
 * The query parameters accepted by the verification count endpoints.
 */
export type GetVerificationCountsParams = {
    /**
     * Optionally restrict the aggregation to a single task list. When
     * omitted, counts across every list the caller can see are returned.
     */
    listId?: number | undefined;
    dateFrom?: Date | undefined;
    dateTo?: Date | undefined;

    /**
     * Scope the counts to statuses reported against the given node — both
     * the node's own Uber and any legacy referenced Uber are matched.
     */
    nodeId?: number | undefined;
};

/**
 * Queries the API for task list item statuses within a date window, with
 * optional filtering by item and verification presence.
 */
export async function queryTaskListItemStatusesAsync(
    params: QueryTaskListItemStatusesParams,
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<TaskListItemStatusWithDetails>> {
    const response = await api.get<Spintr.QueryEnvelope<TaskListItemStatusWithDetails>>(
        statusBaseUrl, {
        cancelToken,
        params,
    });

    return {
        ...response.data,
        items: response.data.items.map((status) => ({
            ...status,
            reportedOn: new Date(status.reportedOn),
        })),
    };
}

/**
 * Returns the number of verification files submitted per station for the
 * given task list within a window.
 */
export async function getStationVerificationCountsAsync(
    params: GetVerificationCountsParams,
    cancelToken?: CancelToken,
): Promise<StationVerificationCount[]> {
    const response = await api.get<StationVerificationCount[]>(
        `${statusBaseUrl}/verification-counts/by-station`, {
        cancelToken,
        params,
    });

    return response.data;
}

/**
 * Returns the number of verification files submitted per task for the given
 * task list within a window.
 */
export async function getTaskVerificationCountsAsync(
    params: GetVerificationCountsParams,
    cancelToken?: CancelToken,
): Promise<TaskVerificationCount[]> {
    const response = await api.get<TaskVerificationCount[]>(
        `${statusBaseUrl}/verification-counts/by-task`, {
        cancelToken,
        params,
    });

    return response.data;
}

/**
 * Builds the URL of a verification file for use as an `<img>` src or `<a>`
 * href. Authentication is handled by the shared session cookie.
 *
 * @param {string} verificationId - The unique identifier of the verification.
 * @param {{ download?: boolean }} [options] - When `download` is true the
 * server adds a `Content-Disposition` filename so the browser saves rather
 * than renders.
 */
export function getVerificationFileUrl(
    verificationId: string,
    options?: { download?: boolean },
): string {
    const query = options?.download ? "?download=true" : "";
    return `${statusBaseUrl}/verifications/${verificationId}${query}`;
}

/**
 * Creates a task list item status asynchronously.
 *
 * @param {TaskStatusModel} model - The task status model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListItemStatus>} - The created task list item status.
 */
export async function createTaskListItemStatusAsync(
    model: TaskStatusModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListItemStatus> {
    const formData = new FormData();
    formData.append("itemId", model.itemId.toString());
    formData.append("status", model.status.toString());
    formData.append("artifactValue", model.artifactValue ?? "");

    if (model.files) {
        model.files.forEach((file) => {
            formData.append("files", file, file.name);
        });
    }

    if (model.pinCode !== undefined && model.pinCode !== null) {
        formData.append("pinCode", model.pinCode);
    }

    if (model.reporterId !== undefined && model.reporterId !== null) {
        formData.append("reporterId", model.reporterId.toString());
    }

    const response = await api.post<Spintr.TaskListItemStatus>(
        statusBaseUrl,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return response.data;
}

/**
 * Updates a task list item status asynchronously.
 *
 * @param {number} id - The ID of the task list item status to be updated.
 * @param {TaskStatusModel} model - The task status model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListItemStatus>} - The updated task list item status.
 */
export async function updateTaskListItemStatusAsync(
    id: number,
    model: TaskStatusModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListItemStatus> {
    const formData = new FormData();
    formData.append("itemId", model.itemId.toString());
    formData.append("status", model.status.toString());
    formData.append("artifactValue", model.artifactValue ?? "");

    if (model.files) {
        model.files.forEach((file) => {
            formData.append("files", file, file.name);
        });
    }

    if (model.pinCode !== undefined && model.pinCode !== null) {
        formData.append("pinCode", model.pinCode);
    }

    if (model.reporterId !== undefined && model.reporterId !== null) {
        formData.append("reporterId", model.reporterId.toString());
    }

    const response = await api.put<Spintr.TaskListItemStatus>(
        `${statusBaseUrl}/${id}`,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return response.data;
}

/**
 * Deletes a task list item status asynchronously.
 *
 * @param {number} id - The ID of the task list item status to be deleted.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<void>} - A promise that resolves when the deletion is complete.
 */
export async function deleteTaskListItemStatusAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(
        `${statusBaseUrl}/${id}`, {
        cancelToken,
    });
}

export async function queryQualityHubPinHashesAsync(
    cancelToken?: CancelToken,
): Promise<QualityHubPinHash[]> {
    const response = await api.get<QualityHubPinHash[]>(
        "/api/v1/QualityHubPins/hashes",
        { cancelToken });

    return response.data;
}

export async function resetQualityHubPinAsync(
    userId: number,
    pinCode: string,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.post<void>(
        `/api/v1/QualityHubPins/users/${userId}/reset`,
        { pinCode },
        { cancelToken });
}

/**
 * Queries the API for resource tags.
 *
 * @param {number} [skip=0] - The number of items to skip.
 * @param {number} [take=100] - The number of items to take.
 * @param {string} [search] - The search term to filter the tags.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.QueryEnvelope<Spintr.ResourceTag>>} - A promise that resolves to the resource tags.
 */
export async function queryResourceTagsAsync(
    skip: number = 0,
    take: number = 100,
    search?: string,
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<Spintr.ResourceTag>> {
    const params: AxiosRequestConfig = {
        params: {
            skip,
            take,
            search,
        },
        cancelToken,
    };

    const response = await api.get<Spintr.QueryEnvelope<Spintr.ResourceTag>>(
        resourceTagsBaseUrl, params,
    );

    return response.data;
}

/**
 * Finds a resource tag by ID asynchronously.
 *
 * @param {number} id - The ID of the resource tag to be found.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.ResourceTag>} - The found resource tag.
 * */
export async function findResourceTagAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<Spintr.ResourceTag> {
    const response = await api.get<Spintr.ResourceTag>(
        `${resourceTagsBaseUrl}/${id}`, {
        cancelToken,
    });

    return response.data;
}

/**
 * Creates a resource tag asynchronously.
 *
 * @param {ResourceTagModel} model - The resource tag model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.ResourceTag>} - The created resource tag.
 */
export async function createResourceTagAsync(
    model: ResourceTagModel,
    cancelToken?: CancelToken,
): Promise<Spintr.ResourceTag> {
    const response = await api.post<Spintr.ResourceTag>(
        resourceTagsBaseUrl, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Updates a resource tag asynchronously.
 *
 * @param {number} id - The ID of the resource tag to be updated.
 * @param {ResourceTagModel} model - The resource tag model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.ResourceTag>} - The updated resource tag.
 */
export async function updateResourceTagAsync(
    id: number,
    model: ResourceTagModel,
    cancelToken?: CancelToken,
): Promise<Spintr.ResourceTag> {
    const response = await api.put<Spintr.ResourceTag>(
        `${resourceTagsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Deletes a resource tag asynchronously.
 *
 * @param {number} id - The ID of the resource tag to be deleted.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<void>} - A promise that resolves when the deletion is complete.
 */
export async function deleteResourceTagAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(
        `${resourceTagsBaseUrl}/${id}`, {
        cancelToken,
    });
}

function fixOperationalTargetDates(opsTarget: OperationalTargetResponse): Spintr.OperationalTarget {
    return {
        ...opsTarget,
        startDate: new Date(opsTarget.startDate),
        endDate: opsTarget.endDate ? new Date(opsTarget.endDate) : undefined,
    };
}

export async function queryOperationalTargetsAsync(
    signal?: AbortSignal,
): Promise<Spintr.QueryEnvelope<Spintr.OperationalTarget>> {
    const response = await api.get<Spintr.QueryEnvelope<OperationalTargetResponse>>(
        operationalTargetsBaseUrl,
        {
            signal,
        });

    return {
        ...response.data,
        items: response.data.items.map(fixOperationalTargetDates),
    };
}

export async function findOperationalTargetAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<Spintr.OperationalTarget> {
    const response = await api.get<OperationalTargetResponse>(
        `${operationalTargetsBaseUrl}/${id}`, {
        cancelToken,
    });

    return fixOperationalTargetDates(response.data);
}

export async function createOperationalTargetAsync(
    model: OperationalTargetModel,
    cancelToken?: CancelToken,
): Promise<Spintr.OperationalTarget> {
    const response = await api.post<OperationalTargetResponse>(
        operationalTargetsBaseUrl, model, {
        cancelToken,
    });

    return fixOperationalTargetDates(response.data);
}

export async function updateOperationalTargetAsync(
    id: number,
    model: OperationalTargetModel,
    cancelToken?: CancelToken,
): Promise<Spintr.OperationalTarget> {
    const response = await api.put<OperationalTargetResponse>(
        `${operationalTargetsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return fixOperationalTargetDates(response.data);
}

export async function deleteOperationalTargetAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(
        `${operationalTargetsBaseUrl}/${id}`, {
        cancelToken,
    });
}

type QualityHubReportDto = Omit<Spintr.QualityHubReport, "date"> & {
    date: string;
};

type QualityHubReportsParams = {
    startDate: Date;
    endDate: Date;
    nodeId?: number;
    listId?: number;
};

export type QualityHubNodeDashboardStatisticsParams = {
    startDate: Date;
    endDate: Date;
    nodeId: number;
};

export async function getQualityHubReportsSummary(
    params?: QualityHubReportsParams,
    cancelToken?: CancelToken,
): Promise<Spintr.QualityHubReport[]> {
    const response = await api.get<QualityHubReportDto[]>(
        "/api/v1/QualityHubReports/summary",
        { 
            params,
            cancelToken,
        });

    return response.data.map((report) => ({
        ...report,
        date: new Date(report.date),
    }));
}

export type QualityHubUnitBreakdownParams = {
    startDate: Date;
    endDate: Date;
    nodeId?: number;
    listId?: number;
    page?: number;
    pageSize?: number;
    rehydrateReports?: boolean;
};

export type QualityHubTaskBreakdownParams = {
    startDate: Date;
    endDate: Date;
    nodeId?: number;
    page?: number;
    pageSize?: number;
    rehydrateReports?: boolean;
};

export async function getQualityHubUnitBreakdownReportAsync(
    params?: QualityHubUnitBreakdownParams,
    signal?: AbortSignal,
): Promise<Spintr.QualityHubUnitBreakdownReport> {
    const response = await api.get<Spintr.QualityHubUnitBreakdownReport>(
        "/api/v1/QualityHubReports/breakdown/units",
        { 
            params,
            signal,
        });

    return response.data;
}

export async function getQualityHubListBreakdownReportAsync(
    params?: QualityHubUnitBreakdownParams,
    signal?: AbortSignal,
): Promise<Spintr.QualityHubUnitBreakdownReport> {
    const response = await api.get<Spintr.QualityHubUnitBreakdownReport>(
        `/api/v1/QualityHubReports/breakdown/lists/`,
        { 
            params,
            signal,
        });

    return response.data;
}

export async function getQualityHubTaskBreakdownReportAsync(
    listId: number,
    params?: QualityHubTaskBreakdownParams,
    signal?: AbortSignal,
): Promise<Spintr.QualityHubTaskBreakdownReport> {
    const response = await api.get<Spintr.QualityHubTaskBreakdownReport>(
        `/api/v1/QualityHubReports/breakdown/lists/${listId}`,
        {  params, signal, });

    return response.data;
}

export async function getQualityHubTaskNodeBreakdownReportAsync(
    taskId: number,
    params?: QualityHubTaskBreakdownParams,
    signal?: AbortSignal,
): Promise<Spintr.QualityHubTaskBreakdownReport> {
    const response = await api.get<Spintr.QualityHubTaskBreakdownReport>(
        `/api/v1/QualityHubReports/breakdown/tasks/${taskId}`,
        {  params, signal, });

    return response.data;
}

export type QualityHubActionTimeSeriesEntry = {
    date: Date;
    value: number;
}

export type QualityHubActionSeriesResponse = {
    backlog: QualityHubActionTimeSeriesEntry[];
    dailyRate: QualityHubActionTimeSeriesEntry[];
};

export async function getQualityHubActionSeriesAsync(
    startDate: Date,
    endDate: Date,
    nodeId: number,
    signal?: AbortSignal,
): Promise<QualityHubActionSeriesResponse> {
    const response = await api.get<QualityHubActionSeriesResponse>(
        `/api/v1/QualityHubStatistics/action-series`,
        {  params: { startDate, endDate, nodeId }, signal, });

    return response.data;
}

export type QualityHubActionBurndownResult = {
    completed: number;
    created: number;
};

export async function getQualityHubActionBurndownAsync(
    startDate: Date,
    endDate: Date,
    nodeId: number,
    signal?: AbortSignal,
): Promise<QualityHubActionBurndownResult> {
    const response = await api.get<QualityHubActionBurndownResult>(
        `/api/v1/QualityHubStatistics/action-burndown`,
        {  params: { startDate, endDate, nodeId }, signal, });

    return response.data;
}

export type QualityHubTasksBurndownResult = {
    totalCompleted: number;
    totalDue: number;
};

export type QualityHubAdminDashboardTaskTrendPoint = {
    date: Date;
    due: number;
    completed: number;
};

type QualityHubAdminDashboardTaskTrendPointDto = Omit<QualityHubAdminDashboardTaskTrendPoint, "date"> & {
    date: string;
};

export type QualityHubAdminDashboardTaskTrendResponse = {
    points: QualityHubAdminDashboardTaskTrendPoint[];
};

export type QualityHubAdminDashboardWidget = {
    key: string;
    title: string;
    titleKey?: string | undefined;
    value: number;
    isPercent: boolean;
    href?: string | undefined;
};

export type QualityHubAdminDashboardSection = {
    key: string;
    title: string;
    titleKey?: string | undefined;
    widgets: QualityHubAdminDashboardWidget[];
};

export type QualityHubAdminDashboardResponse = {
    nodeActivity: QualityHubAdminDashboardSection;
    listActivity: QualityHubAdminDashboardSection;
    actions: QualityHubAdminDashboardSection;
};

export enum QualityHubAdminDashboardActivityLevel {
    Good = 1,
    Moderate = 2,
    Bad = 3,
}

export type QualityHubAdminDashboardListItem = {
    key: string;
    primaryText: string;
    secondaryText: string;
    secondaryTextKey?: string | undefined;
    activityLevel?: QualityHubAdminDashboardActivityLevel | undefined;
    tertiaryText: string;
};

export type QualityHubAdminDashboardThresholds = {
    oldActionThreshold: Date;
    recentThreshold: Date;
};

export async function getQualityHubAdminDashboardAsync(
    thresholds: QualityHubAdminDashboardThresholds,
    signal?: AbortSignal,
): Promise<QualityHubAdminDashboardResponse> {
    const response = await api.get<QualityHubAdminDashboardResponse>(
        "/api/v1/QualityHubStatistics/admin-dashboard",
        {
            params: thresholds,
            signal,
        });

    return response.data;
}

export async function getQualityHubAdminDashboardItemsAsync(
    section: string,
    widgetKey: string,
    page: number,
    pageSize: number,
    thresholds: QualityHubAdminDashboardThresholds,
    signal?: AbortSignal,
): Promise<Spintr.QueryEnvelope<QualityHubAdminDashboardListItem>> {
    const response = await api.get<Spintr.QueryEnvelope<QualityHubAdminDashboardListItem>>(
        `/api/v1/QualityHubStatistics/admin-dashboard/${section}/items`,
        {
            params: {
                widgetKey,
                page,
                pageSize,
                ...thresholds,
            },
            signal,
        });

    return response.data;
}

export async function getQualityHubAdminDashboardTaskTrendAsync(
    startDate: Date,
    endDate: Date,
    rehydrateReports = false,
    signal?: AbortSignal,
): Promise<QualityHubAdminDashboardTaskTrendResponse> {
    const response = await api.get<{ points: QualityHubAdminDashboardTaskTrendPointDto[] }>(
        "/api/v1/QualityHubStatistics/admin-dashboard/task-trend",
        {
            params: {
                startDate,
                endDate,
                rehydrateReports,
            },
            signal,
        });

    return {
        points: response.data.points.map((point) => ({
            ...point,
            date: new Date(point.date),
        })),
    };
}

export async function getQualityHubTasksBurndownAsync(
    startDate: Date,
    endDate: Date,
    nodeId: number, 
    signal?: AbortSignal,
): Promise<QualityHubTasksBurndownResult> {
    const response = await api.get<QualityHubTasksBurndownResult>(
        `/api/v1/QualityHubStatistics/task-burndown`,
        {  params: { startDate, endDate, nodeId }, signal, });

    return response.data;
}
